import { generateAdjustmentsTable } from "../tables/adjustments";
import { generateLeasesTable } from "../tables/leases";
import {
  blueColour,
  defaultFontBold,
  defaultFontItalic,
  defaultFontNormal,
} from "../utils/styling";
import { generateSectionParagraphs } from "../utils/WordExportUtils";

export const getLeasesParagraphs = (data) => {
  const { groupName } = data;

  const leasesTable = generateLeasesTable();
  const secondLeasesTable = generateLeasesTable();
  const adjustmentsTable = generateAdjustmentsTable();

  const paragraphsPart1 = [
    {
      segments: [
        {
          text: "Leases:",
          font: defaultFontBold,
          break: 1,
        },
        {
          text: "Under FRS 102 and (prior to the adoption of IFRS 16) IAS 17, lessees had to classify their leases as either finance leases or operating leases. Whilst IFRS 16 removed this distinction for accounting purposes, replacing it with a concept of “right-of-use’ asset and lease liability, the distinction was preserved for Corporate Interest Restriction purposes for accounting periods beginning on or after 1 January 2019.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "As a result, where groups have adopted IFRS 16 for any UK group companies, it is necessary to consider whether each right-of-use lease would have fallen to be accounted for as a finance lease or an operating lease under an accounting standard that retained this distinction.",
          font: defaultFontNormal,
          break: 2,
        },
        {
          text: "Lease interest expense/income amounts (i.e. finance costs/income implicit in the lease) are only included in Tax-Interest expense/income to the extent that the lease is a finance lease or a right-of-use lease that would have been accounted for as a finance lease under an accounting standard that retained this distinction. All other lease interest/expense amounts are not included as Tax-Interest and so by default are included as Tax-EBITDA.",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
    {
      segments: [
        {
          text: "Application to the ",
          font: defaultFontItalic,
          break: 1,
        },
        {
          text: groupName,
          font: defaultFontItalic,
        },
        {
          text: " group",
          font: defaultFontItalic,
        },
      ],
    },
    {
      segments: [
        {
          text: "Suggested wording if FRS 102 is adopted by all group entities:",
          font: defaultFontNormal,
          color: blueColour,
          break: 1,
        },
        {
          text: "We understand that the accounting standard adopted by all UK group companies is FRS 102, such that no separate lease classification is required for CIR purposes.",
          font: defaultFontNormal,
          color: blueColour,
          break: 1,
        },
        {
          text: "Suggested wording if IFRS/FRS 101 is adopted by all group entities:",
          font: defaultFontNormal,
          color: blueColour,
          break: 2,
        },
        {
          text: "We understand that the accounting standard adopted by all UK group companies is IFRS/FRS 101 [Delete IFRS/FRS 101 as appropriate], such that separate finance/operating lease classification is required in respect of the right-of-use leases for CIR purposes.",
          font: defaultFontNormal,
          color: blueColour,
          break: 1,
        },
        {
          text: "[Relationship team to include appropriate wording to set out work undertaken to classify between operating and finance leases. Where this information has been provided by the client, the following disclaimer is recommended, adapted as required: “The classification of right-of-use leases between operating and finance leases falls outside our scope of work. You have informed us that the amount of right-of-use interest expense and depreciation claimed as deductible under SP3/91 that is attributable to leases that would fall to be treated as finance leases is as set out below:]",
          font: defaultFontNormal,
          color: blueColour,
          break: 2,
        },
      ],
    },
  ];

  const paragraphsPart2 = [
    {
      segments: [
        {
          text: "Suggested wording if a mix of FRS 102/IFRS/FRS 101 is adopted:",
          font: defaultFontNormal,
          color: blueColour,
          break: 1,
        },
        {
          text: "We understand that some UK group companies have adopted IFRS/FRS 101 [Delete IFRS/FRS 101 as appropriate], such that separate finance/operating lease classification is required in respect of the right-of-use leases in these entities for CIR purposes.",
          font: defaultFontNormal,
          color: blueColour,
          break: 1,
        },
        {
          text: "[Relationship team to include appropriate wording to set out work undertaken to classify between operating and finance leases. Where this information has been provided by the client, the following disclaimer is recommended, adapted as required: “The classification of right-of-use leases between operating and finance leases falls outside our scope of work. You have informed us that the amount of right-of-use interest expense and depreciation claimed as deductible under SP3/91 that is attributable to leases that would fall to be treated as finance leases is as set out below:",
          font: defaultFontNormal,
          color: blueColour,
          break: 2,
        },
      ],
    },
  ];

  const paragraphsPart3 = [
    {
      segments: [
        {
          text: "[Approach to s460]",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "We understand that certain companies within the Group are party to Long Funding ",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "[Operating/Finance/Operating and Finance (delete as appropriate)]",
          font: defaultFontNormal,
          color: blueColour,
        },
        {
          text: " leases.",
          font: defaultFontNormal,
        },
        {
          text: "As a result, in calculating the Tax-EBITDA of the relevant companies, the adjustments shown in the table below have been made in accordance with s460 TIOPA 2010 to ignore (a) the amount of any deduction arising under s363 CTA 2010; (b) the amount by which a deduction is reduced under s379 CTA 2010; (c) the capital component of the company's rental earnings under a finance lease which is not a long funding finance lease; and (d) the amount of depreciation in respect of any asset leased to the company under a finance lease which is not a long funding finance lease.",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
  ];

  const paragraphsPart4 = [
    {
      segments: [
        {
          text: "[Relationship team to include appropriate wording to set out work undertaken to identify the adjustments required. Where this information has been provided by the client, the following disclaimer is recommended, adapted as required: “The identification of the adjustments required under s460 TIOPA 2010 falls outside our scope of work. You have informed us of the amount of the adjustments that are required, as set out below:",
          font: defaultFontNormal,
          color: blueColour,
          break: 1,
        },
      ],
    },
    {
      segments: [
        {
          text: "[Approach to s460]",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
  ];

  return [
    ...generateSectionParagraphs(paragraphsPart1),
    ...leasesTable,
    ...generateSectionParagraphs(paragraphsPart2),
    ...secondLeasesTable,
    ...generateSectionParagraphs(paragraphsPart3),
    ...adjustmentsTable,
    ...generateSectionParagraphs(paragraphsPart4),
  ];
};
